import React, { useState, useEffect } from 'react';
import { X, Save, Star, Calendar, Target, TrendingUp, DollarSign, Clock } from 'lucide-react';
import { supabase } from '@shared/lib/supabase';
import { toast } from '@shared/components/ui/Toaster';
import type { UserSkillWithMaster } from '@shared/types/skills-tracker.types';

interface SkillEditModalProps {
  skill: UserSkillWithMaster | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

interface SkillFormData {
  priority: number;
  current_proficiency_level: string;
  target_proficiency_level: string;
  progress_percentage: number;
  estimated_months_to_complete: number;
  target_completion_date: string;
  status: string;
  development_notes: string;
  roi_salary_impact_estimate: number;
  roi_opportunity_score: number;
  roi_career_advancement_potential: string;
}

const proficiencyLevels = ['none', 'beginner', 'intermediate', 'advanced', 'expert'];
const statusOptions = ['active', 'paused', 'completed', 'abandoned'];
const roiLevels = ['low', 'medium', 'high', 'critical'];

export const SkillEditModal: React.FC<SkillEditModalProps> = ({ skill, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState<SkillFormData>({
    priority: 3,
    current_proficiency_level: 'none',
    target_proficiency_level: 'intermediate',
    progress_percentage: 0,
    estimated_months_to_complete: 6,
    target_completion_date: '',
    status: 'active',
    development_notes: '',
    roi_salary_impact_estimate: 0,
    roi_opportunity_score: 50,
    roi_career_advancement_potential: 'medium'
  });

  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (skill) {
      setFormData({
        priority: skill.priority,
        current_proficiency_level: skill.current_proficiency_level,
        target_proficiency_level: skill.target_proficiency_level,
        progress_percentage: skill.progress_percentage,
        estimated_months_to_complete: skill.estimated_months_to_complete || 6,
        target_completion_date: skill.target_completion_date || '',
        status: skill.status,
        development_notes: skill.development_notes || '',
        roi_salary_impact_estimate: skill.roi_salary_impact_estimate || 0,
        roi_opportunity_score: skill.roi_opportunity_score || 50,
        roi_career_advancement_potential: skill.roi_career_advancement_potential || 'medium'
      });
      setHasChanges(false);
    }
  }, [skill]);

  const handleChange = (field: keyof SkillFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const validateForm = (): string | null => {
    if (formData.priority < 1 || formData.priority > 5) {
      return 'Priority must be between 1 and 5';
    }

    const currentIndex = proficiencyLevels.indexOf(formData.current_proficiency_level);
    const targetIndex = proficiencyLevels.indexOf(formData.target_proficiency_level);

    // Only enforce target > current for active skills that haven't reached expert level
    if (formData.status === 'active' && targetIndex < currentIndex) {
      return 'Target proficiency cannot be lower than current proficiency for active skills';
    }

    if (formData.progress_percentage < 0 || formData.progress_percentage > 100) {
      return 'Progress must be between 0 and 100';
    }

    if (formData.estimated_months_to_complete < 1) {
      return 'Timeline must be at least 1 month';
    }

    return null;
  };

  const handleSave = async () => {
    if (!skill) return;

    const validationError = validateForm();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setSaving(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const updateData = {
        ...formData,
        target_completion_date: formData.target_completion_date || null
      };

      const { error } = await supabase
        .from('user_skills_tracker')
        .update(updateData)
        .eq('id', skill.id)
        .eq('user_id', user.id);

      if (error) throw error;

      // Log the modification
      await supabase.from('user_skill_modifications').insert({
        user_id: user.id,
        user_skill_id: skill.id,
        skill_id: skill.skill_id,
        modification_type: 'proficiency_updated',
        previous_state: {
          priority: skill.priority,
          progress_percentage: skill.progress_percentage,
          current_proficiency_level: skill.current_proficiency_level
        },
        new_state: formData,
        update_source: 'user_manual'
      });

      toast.success('Skill updated successfully!');
      setHasChanges(false);
      onSave();
      onClose();
    } catch (error: any) {
      console.error('Error updating skill:', error);
      toast.error(error.message || 'Failed to update skill');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (hasChanges) {
      if (confirm('You have unsaved changes. Are you sure you want to close?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  if (!isOpen || !skill) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={handleClose} />

        <div className="inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-gray-900">{skill.skill.skill_name}</h3>
              <p className="text-sm text-gray-500 mt-1">
                {skill.skill.category} {skill.skill.subcategory && `• ${skill.skill.subcategory}`}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-6 space-y-6 max-h-[70vh] overflow-y-auto">
            {/* Priority */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <Star className="w-4 h-4" />
                Priority Level
              </label>
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map(level => (
                  <button
                    key={level}
                    onClick={() => handleChange('priority', level)}
                    className={`w-12 h-12 rounded-lg border-2 transition-all ${
                      formData.priority === level
                        ? 'border-yellow-500 bg-yellow-50'
                        : 'border-gray-200 hover:border-yellow-300'
                    }`}
                  >
                    <Star
                      className={`w-6 h-6 mx-auto ${
                        formData.priority === level ? 'fill-yellow-500 text-yellow-500' : 'text-gray-300'
                      }`}
                    />
                  </button>
                ))}
                <span className="ml-2 text-sm text-gray-600">
                  {formData.priority === 1 && 'Critical'}
                  {formData.priority === 2 && 'High'}
                  {formData.priority === 3 && 'Medium'}
                  {formData.priority === 4 && 'Low'}
                  {formData.priority === 5 && 'Optional'}
                </span>
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <Target className="w-4 h-4" />
                Status
              </label>
              <div className="grid grid-cols-4 gap-2">
                {statusOptions.map(status => (
                  <button
                    key={status}
                    onClick={() => handleChange('status', status)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${
                      formData.status === status
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>

            {/* Proficiency Levels */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Current Proficiency
                </label>
                <select
                  value={formData.current_proficiency_level}
                  onChange={(e) => handleChange('current_proficiency_level', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {proficiencyLevels.map(level => (
                    <option key={level} value={level} className="capitalize">
                      {level}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Target Proficiency
                </label>
                <select
                  value={formData.target_proficiency_level}
                  onChange={(e) => handleChange('target_proficiency_level', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {proficiencyLevels.map(level => (
                    <option key={level} value={level} className="capitalize">
                      {level}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Progress */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <TrendingUp className="w-4 h-4" />
                Progress: {formData.progress_percentage}%
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={formData.progress_percentage}
                onChange={(e) => handleChange('progress_percentage', parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>0%</span>
                <span>25%</span>
                <span>50%</span>
                <span>75%</span>
                <span>100%</span>
              </div>
            </div>

            {/* Timeline */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Clock className="w-4 h-4" />
                  Estimated Timeline (months)
                </label>
                <input
                  type="number"
                  min="1"
                  max="60"
                  value={formData.estimated_months_to_complete}
                  onChange={(e) => handleChange('estimated_months_to_complete', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4" />
                  Target Completion Date
                </label>
                <input
                  type="date"
                  value={formData.target_completion_date}
                  onChange={(e) => handleChange('target_completion_date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* ROI Impact */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Career Advancement Potential
              </label>
              <div className="grid grid-cols-4 gap-2">
                {roiLevels.map(level => (
                  <button
                    key={level}
                    onClick={() => handleChange('roi_career_advancement_potential', level)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${
                      formData.roi_career_advancement_potential === level
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <DollarSign className="w-4 h-4" />
                  Expected Salary Impact ($)
                </label>
                <input
                  type="number"
                  min="0"
                  step="1000"
                  value={formData.roi_salary_impact_estimate}
                  onChange={(e) => handleChange('roi_salary_impact_estimate', parseFloat(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., 10000"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Opportunity Score (1-100)
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={formData.roi_opportunity_score}
                  onChange={(e) => handleChange('roi_opportunity_score', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Development Notes */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Development Notes & Motivation
              </label>
              <textarea
                value={formData.development_notes}
                onChange={(e) => handleChange('development_notes', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Add your personal notes, learning goals, and motivation..."
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className={`px-6 py-2 text-sm font-medium text-white rounded-lg transition-colors flex items-center gap-2 ${
                !hasChanges || saving
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
